import type { W_Company_EditCredentialNotificationSettings_RM } from 'ab-api/web/company/editCredentialNotificationSettings';

import type { AccountVM, CompanyViewModel, CredentialVM, UserGroupVM } from 'ab-viewModels/company.viewModel';

import type { TemporaryLaborNotificationsRM } from 'ab-requestModels/notificationSettings.requestModel';

import { getId } from '@acceligentllc/shared/utils/array';
import * as TimeOptionUtils from '@acceligentllc/shared/utils/timeOption';

import { AutoNotifyOption, AutoNotifyOptionLabel } from '@acceligentllc/shared/enums/autoNotifyOption';
import { NotificationPriorityOptions, NotificationPriority } from '@acceligentllc/shared/enums/notificationPriority';
import { TimeOptionValues } from '@acceligentllc/shared/enums/timeOption';

import CompanyFeatureFlagsEnum, { CompanyFeatureFlagsTips } from 'ab-enums/companyFeatureFlags.enum';

interface UserGroupFM {
	id: number;
	name: string;
}

export class GeneralNotifications {
	isEnabled: boolean;
	isEnabledAutoNotificationsOnWorkDays: boolean;
	notificationPriority: NotificationPriority;

	static fromCompanyViewModel(company: CompanyViewModel): GeneralNotifications {
		const { notification } = company;
		return {
			isEnabled: notification.isEnabled,
			isEnabledAutoNotificationsOnWorkDays: notification.isEnabledAutoNotificationsOnWorkDays,
			notificationPriority: notification.notificationPriority,
		};
	}
}

export interface DefaultNotificationTimesForm {
	notifyOnMonday: string;
	notifyOnTuesday: string;
	notifyOnWednesday: string;
	notifyOnThursday: string;
	notifyOnFriday: string;
	notifyOnSaturday: string;
	notifyOnSunday: string;
}

export class DefaultNotificationTimes {
	notifyOnMonday: Nullable<number>;
	notifyOnTuesday: Nullable<number>;
	notifyOnWednesday: Nullable<number>;
	notifyOnThursday: Nullable<number>;
	notifyOnFriday: Nullable<number>;
	notifyOnSaturday: Nullable<number>;
	notifyOnSunday: Nullable<number>;

	static fromCompanyViewModel(company: CompanyViewModel): DefaultNotificationTimesForm {
		const { notification } = company;

		return {
			notifyOnMonday: TimeOptionUtils.toString(notification.notifyOnDay[1]),
			notifyOnTuesday: TimeOptionUtils.toString(notification.notifyOnDay[2]),
			notifyOnWednesday: TimeOptionUtils.toString(notification.notifyOnDay[3]),
			notifyOnThursday: TimeOptionUtils.toString(notification.notifyOnDay[4]),
			notifyOnFriday: TimeOptionUtils.toString(notification.notifyOnDay[5]),
			notifyOnSaturday: TimeOptionUtils.toString(notification.notifyOnDay[6]),
			notifyOnSunday: TimeOptionUtils.toString(notification.notifyOnDay[0]),
		};
	}

	static fromForm(form: DefaultNotificationTimesForm): DefaultNotificationTimes {
		return {
			notifyOnMonday: TimeOptionUtils.toNumber(form.notifyOnMonday),
			notifyOnTuesday: TimeOptionUtils.toNumber(form.notifyOnTuesday),
			notifyOnWednesday: TimeOptionUtils.toNumber(form.notifyOnWednesday),
			notifyOnThursday: TimeOptionUtils.toNumber(form.notifyOnThursday),
			notifyOnFriday: TimeOptionUtils.toNumber(form.notifyOnFriday),
			notifyOnSaturday: TimeOptionUtils.toNumber(form.notifyOnSaturday),
			notifyOnSunday: TimeOptionUtils.toNumber(form.notifyOnSunday),
		};
	}

	static toFrom(model: DefaultNotificationTimes): { notifyOnDay: number[] | undefined; } {
		if (
			model.notifyOnSunday === null
			|| model.notifyOnMonday === null
			|| model.notifyOnTuesday === null
			|| model.notifyOnWednesday === null
			|| model.notifyOnThursday === null
			|| model.notifyOnFriday === null
			|| model.notifyOnSaturday === null
		) {
			return {
				notifyOnDay: undefined,
			};
		}

		return {
			notifyOnDay: [
				model.notifyOnSunday,
				model.notifyOnMonday,
				model.notifyOnTuesday,
				model.notifyOnWednesday,
				model.notifyOnThursday,
				model.notifyOnFriday,
				model.notifyOnSaturday,
			],
		};
	}
}

export interface SpecialEmailsForm {
	enableScheduleBoardEmail: boolean;
	notifyScheduleBoardEmail: string;
	scheduleBoardEmailUserGroup: Nullable<UserGroupFM>;

	enablePastWorkOrdersNotification: boolean;
	pastWorkOrdersNotificationUserGroup: Nullable<UserGroupFM>;

	enableNotifyYesterdaysBoard: boolean;
	notifyYesterdaysBoardAt: string;
	notifyYesterdaysBoardUserGroup: Nullable<UserGroupFM>;

	enableNotifyFailed: boolean;
	notifyFailedAt: string;
	notifyFailedUserGroup: Nullable<UserGroupFM>;

	notifyForUnfinishedOrders: boolean;
	notifyForUnfinishedOrdersUserGroup: Nullable<UserGroupFM>;

	notifyOnOrderCreation: boolean;
	notifyOnOrderCreationUserGroup: Nullable<UserGroupFM>;

	enableUnfinishedPurchaseOrderCreatedNotification: boolean;
	enablePurchaseOrderWithUnreceivedItemsNotification: boolean;

	isSendingInvoiceNotificationsEnabledForCompany: boolean;
	enableInvoiceRemindersNotification: boolean;
	notifyInvoiceRemindersAt: string;
	notifyInvoiceRemindersFrom: string;
	notifyInvoiceRemindersUserGroup: Nullable<UserGroupFM>;

	enableItemLowQuantityNotification: boolean;
	itemLowQuantityNotificationUserGroup: Nullable<UserGroupFM>;

	enableDailyItemLowQuantityNotification: boolean;
	dailyItemLowQuantityNotificationUserGroup: Nullable<UserGroupFM>;

	enableToolRepairNotification: boolean;
	toolRepairNotificationUserGroup: Nullable<UserGroupFM>;

	notifyForOrdersTotalExpenseDaily: boolean;
	notifyForOrdersTotalExpenseDailyUserGroup: Nullable<UserGroupFM>;

	notifyForOrdersTotalExpenseWeekly: boolean;
	notifyForOrdersTotalExpenseWeeklyUserGroup: Nullable<UserGroupFM>;

	notifyForOrdersTotalExpenseMonthly: boolean;
	notifyForOrdersTotalExpenseMonthlyUserGroup: Nullable<UserGroupFM>;

	notifyForDuplicateOrdersMonthly: boolean;
	notifyForDuplicateOrdersMonthlyUserGroup: Nullable<UserGroupFM>;

	notifyForInexpensiveOrdersWeekly: boolean;
	notifyForInexpensiveOrdersWeeklyUserGroup: Nullable<UserGroupFM>;

	notifyForInexpensiveOrdersMonthly: boolean;
	notifyForInexpensiveOrdersMonthlyUserGroup: Nullable<UserGroupFM>;

	notifyForExpensiveOrdersWeekly: boolean;
	notifyForExpensiveOrdersWeeklyUserGroup: Nullable<UserGroupFM>;

	notifyForExpensiveOrdersMonthly: boolean;
	notifyForExpensiveOrdersMonthlyUserGroup: Nullable<UserGroupFM>;
}

export class SpecialEmails {
	enableScheduleBoardEmail: boolean;
	notifyScheduleBoardEmail: Nullable<number>;
	scheduleBoardEmailUserGroup: Nullable<UserGroupFM>;

	enablePastWorkOrdersNotification: boolean;
	pastWorkOrdersNotificationUserGroup: Nullable<UserGroupFM>;

	enableNotifyYesterdaysBoard: boolean;
	notifyYesterdaysBoardAt: Nullable<number>;
	notifyYesterdaysBoardUserGroup: Nullable<UserGroupFM>;

	enableNotifyFailed: boolean;
	notifyFailedAt: Nullable<number>;
	notifyFailedUserGroup: Nullable<UserGroupFM>;

	enableUnfinishedPurchaseOrderCreatedNotification: boolean;
	enablePurchaseOrderWithUnreceivedItemsNotification: boolean;

	enableInvoiceRemindersNotification: boolean;
	notifyInvoiceRemindersAt: Nullable<number>;
	notifyInvoiceRemindersFrom: string;
	notifyInvoiceRemindersUserGroup: Nullable<UserGroupFM>;

	notifyOnOrderCreation: boolean;
	notifyOnOrderCreationUserGroup: Nullable<UserGroupFM>;

	notifyForUnfinishedOrders: boolean;
	notifyForUnfinishedOrdersUserGroup: Nullable<UserGroupFM>;
	enableItemLowQuantityNotification: boolean;
	itemLowQuantityNotificationUserGroup: Nullable<UserGroupFM>;

	enableDailyItemLowQuantityNotification: boolean;
	dailyItemLowQuantityNotificationUserGroup: Nullable<UserGroupFM>;

	enableToolRepairNotification: boolean;
	toolRepairNotificationUserGroup: Nullable<UserGroupFM>;

	notifyForOrdersTotalExpenseDaily: boolean;
	notifyForOrdersTotalExpenseDailyUserGroup: Nullable<UserGroupFM>;

	notifyForOrdersTotalExpenseWeekly: boolean;
	notifyForOrdersTotalExpenseWeeklyUserGroup: Nullable<UserGroupFM>;

	notifyForOrdersTotalExpenseMonthly: boolean;
	notifyForOrdersTotalExpenseMonthlyUserGroup: Nullable<UserGroupFM>;

	notifyForDuplicateOrdersMonthly: boolean;
	notifyForDuplicateOrdersMonthlyUserGroup: Nullable<UserGroupFM>;

	notifyForInexpensiveOrdersWeekly: boolean;
	notifyForInexpensiveOrdersWeeklyUserGroup: Nullable<UserGroupFM>;

	notifyForInexpensiveOrdersMonthly: boolean;
	notifyForInexpensiveOrdersMonthlyUserGroup: Nullable<UserGroupFM>;

	notifyForExpensiveOrdersWeekly: boolean;
	notifyForExpensiveOrdersWeeklyUserGroup: Nullable<UserGroupFM>;

	notifyForExpensiveOrdersMonthly: boolean;
	notifyForExpensiveOrdersMonthlyUserGroup: Nullable<UserGroupFM>;

	static fromCompanyViewModel(company: CompanyViewModel): SpecialEmailsForm {
		const { notification } = company;
		return {
			enableScheduleBoardEmail: notification.enableScheduleBoardEmail,
			notifyScheduleBoardEmail: TimeOptionUtils.toString(notification.notifyScheduleBoardEmail),
			scheduleBoardEmailUserGroup: notification.scheduleBoardEmailUserGroup,
			enablePastWorkOrdersNotification: notification.enablePastWorkOrdersNotification,
			pastWorkOrdersNotificationUserGroup: notification.pastWorkOrdersNotificationUserGroup,
			enableNotifyYesterdaysBoard: notification.enableNotifyYesterdaysBoard,
			notifyYesterdaysBoardAt: TimeOptionUtils.toString(notification.notifyYesterdaysBoardAt),
			notifyYesterdaysBoardUserGroup: notification.notifyYesterdaysBoardUserGroup,
			enableNotifyFailed: notification.enableNotifyFailed,
			notifyFailedAt: TimeOptionUtils.toString(notification.notifyFailedAt),
			notifyFailedUserGroup: notification.notifyFailedUserGroup,
			enableUnfinishedPurchaseOrderCreatedNotification: notification.enableUnfinishedPurchaseOrderCreatedNotification,
			enablePurchaseOrderWithUnreceivedItemsNotification: notification.enablePurchaseOrderWithUnreceivedItemsNotification,
			isSendingInvoiceNotificationsEnabledForCompany: company.isSendingInvoiceNotificationsEnabled,
			enableInvoiceRemindersNotification: notification.enableInvoiceRemindersNotification,
			notifyInvoiceRemindersAt: TimeOptionUtils.toString(notification.notifyInvoiceRemindersAt),
			notifyInvoiceRemindersFrom: notification.notifyInvoiceRemindersFrom,
			notifyInvoiceRemindersUserGroup: notification.notifyInvoiceRemindersUserGroup,
			notifyOnOrderCreation: notification.notifyOnOrderCreation,
			notifyOnOrderCreationUserGroup: notification.notifyOnOrderCreationUserGroup,
			notifyForUnfinishedOrders: notification.notifyForUnfinishedOrders,
			notifyForUnfinishedOrdersUserGroup: notification.notifyForUnfinishedOrdersUserGroup,
			enableItemLowQuantityNotification: notification.enableItemLowQuantityNotification,
			itemLowQuantityNotificationUserGroup: notification.itemLowQuantityNotificationUserGroup,
			enableDailyItemLowQuantityNotification: notification.enableDailyItemLowQuantityNotification,
			dailyItemLowQuantityNotificationUserGroup: notification.dailyItemLowQuantityNotificationUserGroup,
			enableToolRepairNotification: notification.enableToolRepairNotification,
			toolRepairNotificationUserGroup: notification.toolRepairNotificationUserGroup,
			notifyForOrdersTotalExpenseDaily: notification.notifyForOrdersTotalExpenseDaily,
			notifyForOrdersTotalExpenseDailyUserGroup: notification.notifyForOrdersTotalExpenseDailyUserGroup,
			notifyForOrdersTotalExpenseWeekly: notification.notifyForOrdersTotalExpenseWeekly,
			notifyForOrdersTotalExpenseWeeklyUserGroup: notification.notifyForOrdersTotalExpenseWeeklyUserGroup,
			notifyForOrdersTotalExpenseMonthly: notification.notifyForOrdersTotalExpenseMonthly,
			notifyForOrdersTotalExpenseMonthlyUserGroup: notification.notifyForOrdersTotalExpenseMonthlyUserGroup,
			notifyForDuplicateOrdersMonthly: notification.notifyForDuplicateOrdersMonthly,
			notifyForDuplicateOrdersMonthlyUserGroup: notification.notifyForDuplicateOrdersMonthlyUserGroup,
			notifyForInexpensiveOrdersWeekly: notification.notifyForInexpensiveOrdersWeekly,
			notifyForInexpensiveOrdersWeeklyUserGroup: notification.notifyForInexpensiveOrdersWeeklyUserGroup,
			notifyForInexpensiveOrdersMonthly: notification.notifyForInexpensiveOrdersMonthly,
			notifyForInexpensiveOrdersMonthlyUserGroup: notification.notifyForInexpensiveOrdersMonthlyUserGroup,
			notifyForExpensiveOrdersWeekly: notification.notifyForExpensiveOrdersWeekly,
			notifyForExpensiveOrdersWeeklyUserGroup: notification.notifyForExpensiveOrdersWeeklyUserGroup,
			notifyForExpensiveOrdersMonthly: notification.notifyForExpensiveOrdersMonthly,
			notifyForExpensiveOrdersMonthlyUserGroup: notification.notifyForExpensiveOrdersMonthlyUserGroup,
		};
	}

	static fromForm(form: SpecialEmailsForm): SpecialEmails {
		return {
			enableScheduleBoardEmail: form.enableScheduleBoardEmail,
			notifyScheduleBoardEmail: TimeOptionUtils.toNumber(form.notifyScheduleBoardEmail),
			scheduleBoardEmailUserGroup: form.scheduleBoardEmailUserGroup,
			enablePastWorkOrdersNotification: form.enablePastWorkOrdersNotification,
			pastWorkOrdersNotificationUserGroup: form.pastWorkOrdersNotificationUserGroup,
			enableNotifyYesterdaysBoard: form.enableNotifyYesterdaysBoard,
			notifyYesterdaysBoardAt: TimeOptionUtils.toNumber(form.notifyYesterdaysBoardAt),
			notifyYesterdaysBoardUserGroup: form.notifyYesterdaysBoardUserGroup,
			enableNotifyFailed: form.enableNotifyFailed,
			notifyFailedAt: TimeOptionUtils.toNumber(form.notifyFailedAt),
			notifyFailedUserGroup: form.notifyFailedUserGroup,
			enableUnfinishedPurchaseOrderCreatedNotification: form.enableUnfinishedPurchaseOrderCreatedNotification,
			enablePurchaseOrderWithUnreceivedItemsNotification: form.enablePurchaseOrderWithUnreceivedItemsNotification,
			enableInvoiceRemindersNotification: form.enableInvoiceRemindersNotification,
			notifyInvoiceRemindersAt: TimeOptionUtils.toNumber(form.notifyInvoiceRemindersAt),
			notifyInvoiceRemindersFrom: form.notifyInvoiceRemindersFrom,
			notifyInvoiceRemindersUserGroup: form.notifyInvoiceRemindersUserGroup,
			notifyOnOrderCreation: form.notifyOnOrderCreation,
			notifyOnOrderCreationUserGroup: form.notifyOnOrderCreationUserGroup,
			notifyForUnfinishedOrders: form.notifyForUnfinishedOrders,
			notifyForUnfinishedOrdersUserGroup: form.notifyForUnfinishedOrdersUserGroup,
			enableItemLowQuantityNotification: form.enableItemLowQuantityNotification,
			itemLowQuantityNotificationUserGroup: form.itemLowQuantityNotificationUserGroup,
			enableDailyItemLowQuantityNotification: form.enableDailyItemLowQuantityNotification,
			dailyItemLowQuantityNotificationUserGroup: form.dailyItemLowQuantityNotificationUserGroup,
			enableToolRepairNotification: form.enableToolRepairNotification,
			toolRepairNotificationUserGroup: form.toolRepairNotificationUserGroup,
			notifyForOrdersTotalExpenseDaily: form.notifyForOrdersTotalExpenseDaily,
			notifyForOrdersTotalExpenseDailyUserGroup: form.notifyForOrdersTotalExpenseDailyUserGroup,
			notifyForOrdersTotalExpenseWeekly: form.notifyForOrdersTotalExpenseWeekly,
			notifyForOrdersTotalExpenseWeeklyUserGroup: form.notifyForOrdersTotalExpenseWeeklyUserGroup,
			notifyForOrdersTotalExpenseMonthly: form.notifyForOrdersTotalExpenseMonthly,
			notifyForOrdersTotalExpenseMonthlyUserGroup: form.notifyForOrdersTotalExpenseMonthlyUserGroup,
			notifyForDuplicateOrdersMonthly: form.notifyForDuplicateOrdersMonthly,
			notifyForDuplicateOrdersMonthlyUserGroup: form.notifyForDuplicateOrdersMonthlyUserGroup,
			notifyForInexpensiveOrdersWeekly: form.notifyForInexpensiveOrdersWeekly,
			notifyForInexpensiveOrdersWeeklyUserGroup: form.notifyForInexpensiveOrdersWeeklyUserGroup,
			notifyForInexpensiveOrdersMonthly: form.notifyForInexpensiveOrdersMonthly,
			notifyForInexpensiveOrdersMonthlyUserGroup: form.notifyForInexpensiveOrdersMonthlyUserGroup,
			notifyForExpensiveOrdersWeekly: form.notifyForExpensiveOrdersWeekly,
			notifyForExpensiveOrdersWeeklyUserGroup: form.notifyForExpensiveOrdersWeeklyUserGroup,
			notifyForExpensiveOrdersMonthly: form.notifyForExpensiveOrdersMonthly,
			notifyForExpensiveOrdersMonthlyUserGroup: form.notifyForExpensiveOrdersMonthlyUserGroup,
		};
	}
}

export class AutomaticNotifications {
	notifyOnPublish: Nullable<AutoNotifyOption>;
	notifyOnCancel: Nullable<AutoNotifyOption>;
	notifyOnPause: boolean;
	notifyOnResume: boolean;
	notifyUnassignedEmployees: boolean;
	notifyMultipleAssignments: boolean;
	notifySupervisors: boolean;
	notifyOnTimeSheetReject: boolean;
	notifyOnReportReject: boolean;
	enableScheduledNotifications: boolean;

	// Only on FE to evaluate checkbox
	enableAutoNotifyOnPublish?: boolean;
	enableAutoNotifyOnCancel?: boolean;

	enableNotifyQAQC: boolean;
	enableNotifyDeliverableAssignee: boolean;
	enableAutoNotifyDailyTips: boolean;

	static fromCompanyViewModel(company: CompanyViewModel): AutomaticNotifications {
		const { notification } = company;
		return {
			notifyOnPublish: notification.notifyOnPublish ?? AutoNotifyOption.DEFAULT_TIME,
			notifyOnCancel: notification.notifyOnCancel ?? AutoNotifyOption.DEFAULT_TIME,
			notifyOnPause: notification.notifyOnPause,
			notifyOnResume: notification.notifyOnResume,
			notifyUnassignedEmployees: notification.notifyUnassignedEmployees,
			notifyMultipleAssignments: notification.notifyMultipleAssignments,
			notifySupervisors: notification.notifySupervisors,
			enableAutoNotifyOnPublish: !!notification.notifyOnPublish,
			enableAutoNotifyOnCancel: !!notification.notifyOnCancel,
			enableNotifyQAQC: notification.enableNotifyQAQC,
			enableNotifyDeliverableAssignee: notification.enableNotifyDeliverableAssignee,
			enableAutoNotifyDailyTips: notification.enableAutoNotifyDailyTips,
			notifyOnReportReject: notification.notifyOnReportReject,
			notifyOnTimeSheetReject: notification.notifyOnTimeSheetReject,
			enableScheduledNotifications: notification.enableScheduledNotifications,
		};
	}

	static fromForm(form: AutomaticNotifications): AutomaticNotifications {
		return {
			notifyOnPublish: form.enableAutoNotifyOnPublish ? form.notifyOnPublish : null,
			notifyOnCancel: form.enableAutoNotifyOnCancel ? form.notifyOnCancel : null,
			notifyOnPause: form.notifyOnPause,
			notifyOnResume: form.notifyOnResume,
			notifyUnassignedEmployees: form.notifyUnassignedEmployees,
			notifyMultipleAssignments: form.notifyMultipleAssignments,
			notifySupervisors: form.notifySupervisors,
			enableNotifyQAQC: form.enableNotifyQAQC,
			enableNotifyDeliverableAssignee: form.enableNotifyDeliverableAssignee,
			enableAutoNotifyDailyTips: form.enableAutoNotifyDailyTips,
			notifyOnReportReject: form.notifyOnReportReject,
			notifyOnTimeSheetReject: form.notifyOnTimeSheetReject,
			enableScheduledNotifications: form.enableScheduledNotifications,
		};
	}
}

export class CredentialRecipientUserGroupFM {
	id: number;
	name: string;

	constructor(userGroup: UserGroupVM) {
		this.id = userGroup.id;
		this.name = userGroup.name;
	}

	private static _constructorMap = (userGroup: UserGroupVM) => new CredentialRecipientUserGroupFM(userGroup);

	static bulkConstructor = (userGroups: UserGroupVM[]) => userGroups.map(CredentialRecipientUserGroupFM._constructorMap);
}

export class CredentialRecipientAccountFM {
	id: number;
	fullName: string;
	email: string;

	constructor(account: AccountVM) {
		this.id = account.id;
		this.fullName = account.fullName;
		this.email = account.email;
	}

	private static _constructorMap = (account: AccountVM) => new CredentialRecipientAccountFM(account);

	static bulkConstructor = (accounts: AccountVM[]) => accounts.map(CredentialRecipientAccountFM._constructorMap);
}

export class CredentialFM {
	id: number;
	name: string;
	isEnabled: boolean;
	accountRecipients: Nullable<CredentialRecipientAccountFM[]>;
	userGroupRecipients: Nullable<CredentialRecipientUserGroupFM[]>;

	constructor(credential: CredentialVM) {
		this.id = credential.id;
		this.name = credential.name;
		this.isEnabled = credential.isEnabled;
		this.accountRecipients = credential.accountRecipients ? CredentialRecipientAccountFM.bulkConstructor(credential.accountRecipients) : null;
		this.userGroupRecipients = credential.userGroupRecipients ? CredentialRecipientUserGroupFM.bulkConstructor(credential.userGroupRecipients) : null;
	}

	private static _constructorMap = (credential: CredentialVM) => new CredentialFM(credential);

	static bulkConstructor = (credentials: CredentialVM[]) => credentials.map(CredentialFM._constructorMap);

	static bulkToRM = (credentials: CredentialFM[]) => {
		return credentials.map(CredentialFM.toRM);
	};

	static toRM = (credential: CredentialFM): W_Company_EditCredentialNotificationSettings_RM['credentials'][0] => {
		return {
			id: credential.id,
			isEnabled: credential.isEnabled,
			userGroupIds: (credential.userGroupRecipients ?? []).map(getId),
			accountIds: (credential.accountRecipients ?? []).map(getId),
		};
	};
}

export class CompanyCredentialFM {
	credentials: CredentialFM[];

	constructor(credentials: CredentialVM[]) {
		this.credentials = CredentialFM.bulkConstructor(credentials);
	}

	static toRM(form: CompanyCredentialFM): W_Company_EditCredentialNotificationSettings_RM {
		return {
			credentials: CredentialFM.bulkToRM(form.credentials),
		};
	}
}

export class CompanyFeatureFlagsFM {
	isEnabled: boolean;
	isEnabledAutoNotificationsOnWorkDays: boolean;
	notifyOnPublish: AutoNotifyOption;
	notificationPriority: NotificationPriority;
	notifyOnCancel: AutoNotifyOption;
	notifyUnassignedEmployees: boolean;
	enableAutoNotifyOnPublish: boolean;
	enableAutoNotifyOnCancel: boolean;
	notifyOnPause: boolean;
	notifyOnResume: boolean;
	notifyScheduleBoardEmail: string;
	enableScheduleBoardEmail: boolean;
	enablePastWorkOrdersNotification: boolean;
	notifyOnMonday: string;
	notifyOnTuesday: string;
	notifyOnWednesday: string;
	notifyOnThursday: string;
	notifyOnFriday: string;
	notifyOnSaturday: string;
	notifyOnSunday: string;
	notifyMultipleAssignments: boolean;
	notifySupervisors: boolean;
	scheduleBoardEmailUserGroup: Nullable<UserGroupFM>;
	pastWorkOrdersNotificationUserGroup: Nullable<UserGroupFM>;
	enableItemLowQuantityNotification: boolean;
	itemLowQuantityNotificationUserGroup: Nullable<UserGroupFM>;
	enableDailyItemLowQuantityNotification: boolean;
	dailyItemLowQuantityNotificationUserGroup: Nullable<UserGroupFM>;
	notifyYesterdaysBoardAt: string;
	enableNotifyYesterdaysBoard: boolean;
	notifyYesterdaysBoardUserGroup: Nullable<UserGroupFM>;
	notifyFailedAt: string;
	enableNotifyFailed: boolean;
	notifyFailedUserGroup: Nullable<UserGroupFM>;
	enableNotifyQAQC: boolean;
	enableNotifyDeliverableAssignee: boolean;
	enableAutoNotifyDailyTips: boolean;
	notifyOnReportReject: boolean;
	notifyOnTimeSheetReject: boolean;
	notifyTemporaryLabor: boolean;
	enableUnfinishedPurchaseOrderCreatedNotification: boolean;
	enablePurchaseOrderWithUnreceivedItemsNotification: boolean;
	isSendingInvoiceNotificationsEnabled: boolean;
	enableInvoiceRemindersNotification: boolean;
	notifyInvoiceRemindersAt: string;
	notifyInvoiceRemindersFrom: string;
	notifyInvoiceRemindersUserGroup: Nullable<UserGroupFM>;
	enableToolRepairNotification: boolean;
	toolRepairNotificationUserGroup: Nullable<UserGroupFM>;
	notifyOnOrderCreation: boolean;
	notifyOnOrderCreationUserGroup: Nullable<UserGroupFM>;
	notifyForUnfinishedOrders: boolean;
	notifyForUnfinishedOrdersUserGroup: Nullable<UserGroupFM>;
	notifyForOrdersTotalExpenseDaily: boolean;
	notifyForOrdersTotalExpenseDailyUserGroup: Nullable<UserGroupFM>;
	notifyForOrdersTotalExpenseWeekly: boolean;
	notifyForOrdersTotalExpenseWeeklyUserGroup: Nullable<UserGroupFM>;
	notifyForOrdersTotalExpenseMonthly: boolean;
	notifyForOrdersTotalExpenseMonthlyUserGroup: Nullable<UserGroupFM>;
	notifyForDuplicateOrdersMonthly: boolean;
	notifyForDuplicateOrdersMonthlyUserGroup: Nullable<UserGroupFM>;
	notifyForInexpensiveOrdersWeekly: boolean;
	notifyForInexpensiveOrdersWeeklyUserGroup: Nullable<UserGroupFM>;
	notifyForInexpensiveOrdersMonthly: boolean;
	notifyForInexpensiveOrdersMonthlyUserGroup: Nullable<UserGroupFM>;
	notifyForExpensiveOrdersWeekly: boolean;
	notifyForExpensiveOrdersWeeklyUserGroup: Nullable<UserGroupFM>;
	notifyForExpensiveOrdersMonthly: boolean;
	notifyForExpensiveOrdersMonthlyUserGroup: Nullable<UserGroupFM>;
	enableScheduledNotifications: boolean;
	companyCredentialSettings: CredentialFM[];

	constructor(company: CompanyViewModel) {
		const { notification } = company;
		this.isEnabled = notification.isEnabled || false;
		this.isEnabledAutoNotificationsOnWorkDays = notification.isEnabledAutoNotificationsOnWorkDays;
		this.notifyUnassignedEmployees = notification.notifyUnassignedEmployees;
		this.notifyScheduleBoardEmail = TimeOptionUtils.toString(notification.notifyScheduleBoardEmail);
		this.notifyOnMonday = TimeOptionUtils.toString(notification.notifyOnDay[1]);
		this.notifyOnTuesday = TimeOptionUtils.toString(notification.notifyOnDay[2]);
		this.notifyOnWednesday = TimeOptionUtils.toString(notification.notifyOnDay[3]);
		this.notifyOnThursday = TimeOptionUtils.toString(notification.notifyOnDay[4]);
		this.notifyOnFriday = TimeOptionUtils.toString(notification.notifyOnDay[5]);
		this.notifyOnSaturday = TimeOptionUtils.toString(notification.notifyOnDay[6]);
		this.notifyOnSunday = TimeOptionUtils.toString(notification.notifyOnDay[0]);
		this.enableScheduleBoardEmail = this.isEnabled && !!notification.enableScheduleBoardEmail;
		this.enablePastWorkOrdersNotification = this.isEnabled && !!notification.enablePastWorkOrdersNotification;
		this.enableAutoNotifyOnPublish = this.isEnabled && !!notification.notifyOnPublish;
		this.enableAutoNotifyOnCancel = this.isEnabled && !!notification.notifyOnCancel;
		this.notificationPriority = notification.notificationPriority || NotificationPriority.SMS_FIRST;
		this.notifyOnPublish = notification.notifyOnPublish ?? AutoNotifyOption.DEFAULT_TIME;
		this.notifyOnCancel = notification.notifyOnCancel ?? AutoNotifyOption.DEFAULT_TIME;
		this.notifyOnPause = notification.notifyOnPause;
		this.notifyOnResume = notification.notifyOnResume;
		this.enableScheduledNotifications = notification.enableScheduledNotifications;
		this.notifyMultipleAssignments = notification.notifyMultipleAssignments;
		this.notifySupervisors = notification.notifySupervisors;
		this.scheduleBoardEmailUserGroup = notification.scheduleBoardEmailUserGroup;
		this.pastWorkOrdersNotificationUserGroup = notification.pastWorkOrdersNotificationUserGroup;
		this.notifyYesterdaysBoardAt = TimeOptionUtils.toString(notification.notifyYesterdaysBoardAt);
		this.enableNotifyYesterdaysBoard = notification.enableNotifyYesterdaysBoard;
		this.notifyYesterdaysBoardUserGroup = notification.notifyYesterdaysBoardUserGroup;
		this.notifyFailedAt = TimeOptionUtils.toString(notification.notifyFailedAt);
		this.enableNotifyFailed = notification.enableNotifyFailed;
		this.notifyFailedUserGroup = notification.notifyFailedUserGroup;
		this.enableNotifyQAQC = notification.enableNotifyQAQC;
		this.enableNotifyDeliverableAssignee = notification.enableNotifyDeliverableAssignee;
		this.enableAutoNotifyDailyTips = notification.enableAutoNotifyDailyTips;
		this.notifyOnReportReject = notification.notifyOnReportReject;
		this.notifyOnTimeSheetReject = notification.notifyOnTimeSheetReject;
		this.notifyTemporaryLabor = notification.notifyTemporaryLabor;
		this.enableUnfinishedPurchaseOrderCreatedNotification = notification.enableUnfinishedPurchaseOrderCreatedNotification;
		this.enablePurchaseOrderWithUnreceivedItemsNotification = notification.enablePurchaseOrderWithUnreceivedItemsNotification;
		this.isSendingInvoiceNotificationsEnabled = company.isSendingInvoiceNotificationsEnabled;
		this.enableInvoiceRemindersNotification = notification.enableInvoiceRemindersNotification;
		this.notifyInvoiceRemindersAt = TimeOptionUtils.toString(notification.notifyInvoiceRemindersAt);
		this.notifyInvoiceRemindersFrom = notification.notifyInvoiceRemindersFrom;
		this.notifyInvoiceRemindersUserGroup = notification.notifyInvoiceRemindersUserGroup;
		this.notifyOnOrderCreation = notification.notifyOnOrderCreation;
		this.notifyOnOrderCreationUserGroup = notification.notifyOnOrderCreationUserGroup;
		this.notifyForUnfinishedOrders = notification.notifyForUnfinishedOrders;
		this.notifyForUnfinishedOrdersUserGroup = notification.notifyForUnfinishedOrdersUserGroup;

		this.enableItemLowQuantityNotification = this.isEnabled && !!notification.enableItemLowQuantityNotification;
		this.itemLowQuantityNotificationUserGroup = notification.itemLowQuantityNotificationUserGroup;

		this.enableDailyItemLowQuantityNotification = this.isEnabled && !!notification.enableDailyItemLowQuantityNotification;
		this.dailyItemLowQuantityNotificationUserGroup = notification.dailyItemLowQuantityNotificationUserGroup;
		this.enableToolRepairNotification = this.isEnabled && !!notification.enableToolRepairNotification;
		this.toolRepairNotificationUserGroup = notification.toolRepairNotificationUserGroup;
		this.notifyForOrdersTotalExpenseDaily = notification.notifyForOrdersTotalExpenseDaily;
		this.notifyForOrdersTotalExpenseDailyUserGroup = notification.notifyForOrdersTotalExpenseDailyUserGroup;
		this.notifyForOrdersTotalExpenseWeekly = notification.notifyForOrdersTotalExpenseWeekly;
		this.notifyForOrdersTotalExpenseWeeklyUserGroup = notification.notifyForOrdersTotalExpenseWeeklyUserGroup;
		this.notifyForOrdersTotalExpenseMonthly = notification.notifyForOrdersTotalExpenseMonthly;
		this.notifyForOrdersTotalExpenseMonthlyUserGroup = notification.notifyForOrdersTotalExpenseMonthlyUserGroup;
		this.notifyForDuplicateOrdersMonthly = notification.notifyForDuplicateOrdersMonthly;
		this.notifyForDuplicateOrdersMonthlyUserGroup = notification.notifyForDuplicateOrdersMonthlyUserGroup;
		this.notifyForInexpensiveOrdersWeekly = notification.notifyForInexpensiveOrdersWeekly;
		this.notifyForInexpensiveOrdersWeeklyUserGroup = notification.notifyForInexpensiveOrdersWeeklyUserGroup;
		this.notifyForInexpensiveOrdersMonthly = notification.notifyForInexpensiveOrdersMonthly;
		this.notifyForInexpensiveOrdersMonthlyUserGroup = notification.notifyForInexpensiveOrdersMonthlyUserGroup;
		this.notifyForExpensiveOrdersWeekly = notification.notifyForExpensiveOrdersWeekly;
		this.notifyForExpensiveOrdersWeeklyUserGroup = notification.notifyForExpensiveOrdersWeeklyUserGroup;
		this.notifyForExpensiveOrdersMonthly = notification.notifyForExpensiveOrdersMonthly;
		this.notifyForExpensiveOrdersMonthlyUserGroup = notification.notifyForExpensiveOrdersMonthlyUserGroup;

		this.companyCredentialSettings = CredentialFM.bulkConstructor(notification.companyCredentials ?? []);
	}

	static fromCompanyViewModel(company: CompanyViewModel): CompanyFeatureFlagsFM {
		return new CompanyFeatureFlagsFM(company);
	}
}

export class TemporaryLaborNotificationsForm {
	notifyTemporaryLabor: boolean;

	static fromCompanyViewModel(company: CompanyViewModel): TemporaryLaborNotificationsForm {
		const { notification } = company;
		return {
			notifyTemporaryLabor: notification.notifyTemporaryLabor,
		};
	}

	static toRequestModel(form: TemporaryLaborNotificationsForm): TemporaryLaborNotificationsRM {
		return {
			notifyTemporaryLabor: form.notifyTemporaryLabor,
		};
	}
}

class CompanyFeatureFlagsVM {
	isEnabled: boolean;
	isEnabledAutoNotificationsOnWorkDays: boolean;
	notifyOnPublish: AutoNotifyOption;
	notificationPriority: NotificationPriority;
	notifyOnCancel: AutoNotifyOption;
	notifyUnassignedEmployees: boolean;
	enableAutoNotifyOnPublish: boolean;
	enableAutoNotifyOnCancel: boolean;
	notifyOnPause: boolean;
	notifyOnResume: boolean;
	notifyScheduleBoardEmail: string;
	enableScheduleBoardEmail: boolean;
	enablePastWorkOrdersNotification: boolean;
	notifyOnMonday: string;
	notifyOnTuesday: string;
	notifyOnWednesday: string;
	notifyOnThursday: string;
	notifyOnFriday: string;
	notifyOnSaturday: string;
	notifyOnSunday: string;
	notifyMultipleAssignments: boolean;
	notifySupervisors: boolean;
	scheduleBoardEmailUserGroup: Nullable<UserGroupVM>;
	pastWorkOrdersNotificationUserGroup: Nullable<UserGroupVM>;
	enableItemLowQuantityNotification: boolean;
	itemLowQuantityNotificationUserGroup: Nullable<UserGroupVM>;
	enableDailyItemLowQuantityNotification: boolean;
	dailyItemLowQuantityNotificationUserGroup: Nullable<UserGroupVM>;
	notifyYesterdaysBoardAt: string;
	enableNotifyYesterdaysBoard: boolean;
	notifyYesterdaysBoardUserGroup: Nullable<UserGroupVM>;
	notifyFailedAt: string;
	enableNotifyFailed: boolean;
	notifyFailedUserGroup: Nullable<UserGroupVM>;
	enableNotifyQAQC: boolean;
	enableNotifyDeliverableAssignee: boolean;
	enableAutoNotifyDailyTips: boolean;
	notifyOnReportReject: boolean;
	notifyOnTimeSheetReject: boolean;
	notifyTemporaryLabor: boolean;
	enableUnfinishedPurchaseOrderCreatedNotification: boolean;
	enablePurchaseOrderWithUnreceivedItemsNotification: boolean;
	notifyInvoiceRemindersAt: string;
	notifyInvoiceRemindersFrom: string;
	notifyInvoiceRemindersUserGroup: Nullable<UserGroupVM>;
	enableInvoiceRemindersNotification: boolean;
	isSendingInvoiceNotificationsEnabled: boolean;
	enableToolRepairNotification: boolean;
	toolRepairNotificationUserGroup: Nullable<UserGroupVM>;
	notifyOnOrderCreation: boolean;
	notifyOnOrderCreationUserGroup: Nullable<UserGroupVM>;
	notifyForUnfinishedOrders: boolean;
	notifyForUnfinishedOrdersUserGroup: Nullable<UserGroupVM>;
	notifyForOrdersTotalExpenseDaily: boolean;
	notifyForOrdersTotalExpenseDailyUserGroup: Nullable<UserGroupVM>;
	notifyForOrdersTotalExpenseWeekly: boolean;
	notifyForOrdersTotalExpenseWeeklyUserGroup: Nullable<UserGroupVM>;
	notifyForOrdersTotalExpenseMonthly: boolean;
	notifyForOrdersTotalExpenseMonthlyUserGroup: Nullable<UserGroupVM>;
	notifyForDuplicateOrdersMonthly: boolean;
	notifyForDuplicateOrdersMonthlyUserGroup: Nullable<UserGroupVM>;
	notifyForInexpensiveOrdersWeekly: boolean;
	notifyForInexpensiveOrdersWeeklyUserGroup: Nullable<UserGroupVM>;
	notifyForInexpensiveOrdersMonthly: boolean;
	notifyForInexpensiveOrdersMonthlyUserGroup: Nullable<UserGroupVM>;
	notifyForExpensiveOrdersWeekly: boolean;
	notifyForExpensiveOrdersWeeklyUserGroup: Nullable<UserGroupVM>;
	notifyForExpensiveOrdersMonthly: boolean;
	notifyForExpensiveOrdersMonthlyUserGroup: Nullable<UserGroupVM>;
	enableScheduledNotifications: boolean;
	companyCredentialSettings: CredentialVM[];
}

export class CompanyFeatureFlagsEntry {
	key: string;
	label: string;
	checked?: boolean;
	showStatusIcon?: boolean;
	indent?: boolean;
	value?: Nullable<string>;
	hint?: string;

	constructor(key: string, label: string, checked: boolean, value: Nullable<string>, indent: boolean = false, showCheckbox: boolean = true, hint?: string) {
		this.key = key;
		this.label = label;
		this.checked = checked;
		this.showStatusIcon = showCheckbox;
		this.indent = indent;
		this.value = value;
		this.hint = hint;
	}

	static enableNotificationsEntry(flags: CompanyFeatureFlagsVM) {
		const { isEnabled } = flags;
		return new CompanyFeatureFlagsEntry(
			'isEnabled',
			CompanyFeatureFlagsEnum.enableNotifications,
			isEnabled,
			null
		);
	}

	static enableAutomaticNotificationsOnWorkDaysEntry(flags: CompanyFeatureFlagsVM) {
		const { isEnabledAutoNotificationsOnWorkDays } = flags;
		return new CompanyFeatureFlagsEntry(
			'isEnabledAutoNotificationsOnWorkDays',
			CompanyFeatureFlagsEnum.enableAutomaticNotificationsOnWorkDays,
			isEnabledAutoNotificationsOnWorkDays,
			null,
			false,
			true,
			CompanyFeatureFlagsTips.enableAutomaticNotificationsOnWorkDays
		);
	}

	static autoNotifyAtEntry(day: number, autoNotifyAt: string, label: string) {
		return new CompanyFeatureFlagsEntry(
			`autoNotifyOnDay-${day}`,
			label,
			true,
			TimeOptionValues[autoNotifyAt],
			true,
			false
		);
	}

	static autoNotifyOnDaysEntries(flags: CompanyFeatureFlagsVM) {
		const {
			notifyOnMonday: autoNotifyOnMonday,
			notifyOnTuesday: autoNotifyOnTuesday,
			notifyOnWednesday: autoNotifyOnWednesday,
			notifyOnThursday: autoNotifyOnThursday,
			notifyOnFriday: autoNotifyOnFriday,
			notifyOnSaturday: autoNotifyOnSaturday,
			notifyOnSunday: autoNotifyOnSunday,
			isEnabled,
		} = flags;

		if (!isEnabled) {
			return [];
		}

		return [
			this.autoNotifyAtEntry(1, autoNotifyOnMonday, CompanyFeatureFlagsEnum.notifyOnMonday),
			this.autoNotifyAtEntry(2, autoNotifyOnTuesday, CompanyFeatureFlagsEnum.notifyOnTuesday),
			this.autoNotifyAtEntry(3, autoNotifyOnWednesday, CompanyFeatureFlagsEnum.notifyOnWednesday),
			this.autoNotifyAtEntry(4, autoNotifyOnThursday, CompanyFeatureFlagsEnum.notifyOnThursday),
			this.autoNotifyAtEntry(5, autoNotifyOnFriday, CompanyFeatureFlagsEnum.notifyOnFriday),
			this.autoNotifyAtEntry(6, autoNotifyOnSaturday, CompanyFeatureFlagsEnum.notifyOnSaturday),
			this.autoNotifyAtEntry(0, autoNotifyOnSunday, CompanyFeatureFlagsEnum.notifyOnSunday),
		];
	}

	static autoNotifyOnPublishEntry(flags: CompanyFeatureFlagsVM) {
		const { isEnabled, enableAutoNotifyOnPublish, notifyOnPublish } = flags;

		if (!isEnabled) {
			return null;
		}

		return new CompanyFeatureFlagsEntry(
			'notifyOnPublish',
			CompanyFeatureFlagsEnum.notifyOnPublish,
			enableAutoNotifyOnPublish,
			`at: ${AutoNotifyOptionLabel[notifyOnPublish]}`,
			false,
			true,
			CompanyFeatureFlagsTips.notifyOnPublish
		);
	}

	static autoNotifyOnCancelEntry(flags: CompanyFeatureFlagsVM) {
		const { isEnabled, notifyOnCancel, enableAutoNotifyOnCancel } = flags;

		if (!isEnabled) {
			return null;
		}

		return new CompanyFeatureFlagsEntry(
			'notifyOnCancel',
			CompanyFeatureFlagsEnum.notifyOnCancel,
			enableAutoNotifyOnCancel,
			`at: ${AutoNotifyOptionLabel[notifyOnCancel]}`,
			false,
			true,
			CompanyFeatureFlagsTips.notifyOnCancel
		);
	}

	static autoNotifyOnPauseEntry(flags: CompanyFeatureFlagsVM) {
		const { isEnabled, notifyOnPause } = flags;

		if (!isEnabled) {
			return null;
		}

		return new CompanyFeatureFlagsEntry(
			'notifyOnPause',
			CompanyFeatureFlagsEnum.notifyOnPause,
			isEnabled && !!notifyOnPause,
			'at: Immediate',
			false,
			true,
			CompanyFeatureFlagsTips.notifyOnPause
		);
	}

	static autoNotifyOnResumeEntry(flags: CompanyFeatureFlagsVM) {
		const { isEnabled, notifyOnResume } = flags;

		if (!isEnabled) {
			return null;
		}

		return new CompanyFeatureFlagsEntry(
			'notifyOnResume',
			CompanyFeatureFlagsEnum.notifyOnResume,
			isEnabled && !!notifyOnResume,
			'at: Immediate',
			false,
			true,
			CompanyFeatureFlagsTips.notifyOnResume
		);
	}

	static autoNotifyOnReportReject(flags: CompanyFeatureFlagsVM) {
		const { isEnabled, notifyOnReportReject } = flags;

		if (!isEnabled) {
			return null;
		}

		return new CompanyFeatureFlagsEntry(
			'notifyOnReportReject',
			CompanyFeatureFlagsEnum.notifyOnReportReject,
			isEnabled && !!notifyOnReportReject,
			'at: Immediate',
			false,
			true,
			CompanyFeatureFlagsTips.notifyOnReportReject
		);
	}

	static autoNotifyOnTimeSheetReject(flags: CompanyFeatureFlagsVM) {
		const { isEnabled, notifyOnTimeSheetReject } = flags;

		if (!isEnabled) {
			return null;
		}

		return new CompanyFeatureFlagsEntry(
			'notifyOnResume',
			CompanyFeatureFlagsEnum.notifyOnTimeSheetReject,
			isEnabled && !!notifyOnTimeSheetReject,
			'at: Immediate',
			false,
			true,
			CompanyFeatureFlagsTips.notifyOnTimeSheetReject
		);
	}

	static autoNotifyScheduleBoardEmail(flags: CompanyFeatureFlagsVM) {
		const { notifyScheduleBoardEmail, isEnabled, scheduleBoardEmailUserGroup, enableScheduleBoardEmail } = flags;

		if (!isEnabled) {
			return null;
		}

		return new CompanyFeatureFlagsEntry(
			'notifyScheduleBoardEmail',
			CompanyFeatureFlagsEnum.notifyScheduleBoardEmail,
			enableScheduleBoardEmail,
			`at: ${TimeOptionValues[notifyScheduleBoardEmail]}, to: ${scheduleBoardEmailUserGroup?.name ?? '-'}`,
			false,
			true,
			CompanyFeatureFlagsTips.notifyScheduleBoardEmail
		);
	}

	static autoNotifyUnfinishedPurchaseOrderCreated(flags: CompanyFeatureFlagsVM) {
		const {
			isEnabled,
			enableUnfinishedPurchaseOrderCreatedNotification,
		} = flags;

		if (!isEnabled) {
			return null;
		}

		return new CompanyFeatureFlagsEntry(
			'unfinishedPurchaseOrderCreatedNotification',
			CompanyFeatureFlagsEnum.unfinishedPurchaseOrderCreatedNotification,
			enableUnfinishedPurchaseOrderCreatedNotification,
			'at: Default Time',
			false,
			true,
			CompanyFeatureFlagsTips.unfinishedPurchaseOrderCreatedNotification
		);
	}

	static autoNotifyPurchaseOrderWithUnreceivedItems(flags: CompanyFeatureFlagsVM) {
		const {
			isEnabled,
			enablePurchaseOrderWithUnreceivedItemsNotification,
		} = flags;

		if (!isEnabled) {
			return null;
		}

		return new CompanyFeatureFlagsEntry(
			'purchaseOrderWithUnreceivedItems',
			CompanyFeatureFlagsEnum.purchaseOrderWithUnreceivedItems,
			enablePurchaseOrderWithUnreceivedItemsNotification,
			'at: Default Time',
			false,
			true,
			CompanyFeatureFlagsTips.purchaseOrderWithUnreceivedItems
		);
	}

	static autoNotifyInvoiceReminderNotification(flags: CompanyFeatureFlagsVM) {
		const {
			isEnabled,
			enableInvoiceRemindersNotification,
			notifyInvoiceRemindersAt,
			notifyInvoiceRemindersFrom,
			notifyInvoiceRemindersUserGroup,
		} = flags;

		if (!isEnabled) {
			return null;
		}

		return new CompanyFeatureFlagsEntry(
			'invoiceRemindersNotification',
			CompanyFeatureFlagsEnum.invoiceRemindersNotification,
			enableInvoiceRemindersNotification,
			`at: ${TimeOptionValues[notifyInvoiceRemindersAt]}, from: ${notifyInvoiceRemindersFrom}, to: ${notifyInvoiceRemindersUserGroup?.name ?? '-'}`,
			false,
			true,
			CompanyFeatureFlagsTips.invoiceRemindersNotification
		);
	}

	static autoNotifyFailedNotifications(flags: CompanyFeatureFlagsVM) {
		const { notifyFailedAt, isEnabled, notifyFailedUserGroup, enableNotifyFailed } = flags;

		if (!isEnabled) {
			return null;
		}

		return new CompanyFeatureFlagsEntry(
			'notifyFailedAt',
			CompanyFeatureFlagsEnum.notifyFailedNotifications,
			enableNotifyFailed,
			`at: ${TimeOptionValues[notifyFailedAt]}, to: ${notifyFailedUserGroup?.name ?? '-'}`,
			false,
			true,
			CompanyFeatureFlagsTips.notifyFailedNotifications
		);
	}

	static autoNotifyYesterdaysScheduleBoardEmail(flags: CompanyFeatureFlagsVM) {
		const { notifyYesterdaysBoardAt, isEnabled, notifyYesterdaysBoardUserGroup, enableNotifyYesterdaysBoard } = flags;

		if (!isEnabled) {
			return null;
		}

		return new CompanyFeatureFlagsEntry(
			'notifyYesterdaysBoardAt',
			CompanyFeatureFlagsEnum.notifyYesterdaysScheduleBoardEmail,
			enableNotifyYesterdaysBoard,
			`at: ${TimeOptionValues[notifyYesterdaysBoardAt]}, to: ${notifyYesterdaysBoardUserGroup?.name ?? '-'}`,
			false,
			true,
			CompanyFeatureFlagsTips.notifyYesterdaysScheduleBoardEmail
		);
	}

	static notifyPastWorkOrdersEmail(flags: CompanyFeatureFlagsVM) {
		const { enablePastWorkOrdersNotification, pastWorkOrdersNotificationUserGroup } = flags;
		return new CompanyFeatureFlagsEntry(
			'enablePastWorkOrdersNotificationUserGroup',
			CompanyFeatureFlagsEnum.enablePastWorkOrdersNotification,
			enablePastWorkOrdersNotification,
			`at: On Change, to: ${pastWorkOrdersNotificationUserGroup?.name ?? '-'}`,
			false,
			true,
			CompanyFeatureFlagsTips.enablePastWorkOrdersNotification
		);
	}

	static notifyOnItemDepartmentLowQuantity(flags: CompanyFeatureFlagsVM) {
		const { enableItemLowQuantityNotification, itemLowQuantityNotificationUserGroup } = flags;

		return new CompanyFeatureFlagsEntry(
			'enableItemDepartmentLowQuantityNotification',
			CompanyFeatureFlagsEnum.enableItemLowQuantityNotification,
			enableItemLowQuantityNotification,
			`at: On Change, to: ${itemLowQuantityNotificationUserGroup?.name ?? '-'}`,
			false,
			true,
			CompanyFeatureFlagsTips.enableItemLowQuantityNotification
		);
	}

	static autoNotifyOnItemDepartmentLowQuantity(flags: CompanyFeatureFlagsVM) {
		const { enableDailyItemLowQuantityNotification, dailyItemLowQuantityNotificationUserGroup } = flags;

		return new CompanyFeatureFlagsEntry(
			'enableDailyItemLowQuantityNotification',
			CompanyFeatureFlagsEnum.enableDailyItemLowQuantityNotification,
			enableDailyItemLowQuantityNotification,
			`at: Default Time, to: ${dailyItemLowQuantityNotificationUserGroup?.name ?? '-'}`,
			false,
			true,
			CompanyFeatureFlagsTips.enableDailyItemLowQuantityNotification
		);
	}

	static autoNotifyOnToolRepairModify(flags: CompanyFeatureFlagsVM) {
		const { enableToolRepairNotification, toolRepairNotificationUserGroup } = flags;

		return new CompanyFeatureFlagsEntry(
			'enableToolRepairNotification',
			CompanyFeatureFlagsEnum.enableToolRepairNotification,
			enableToolRepairNotification,
			`at: On Change, to: ${toolRepairNotificationUserGroup?.name ?? '-'}`,
			false,
			true,
			CompanyFeatureFlagsTips.enableToolRepairNotification
		);
	}

	static autoNotifyUnassignedEmployeesEntry(flags: CompanyFeatureFlagsVM) {
		const { isEnabled, notifyUnassignedEmployees } = flags;

		if (!isEnabled) {
			return null;
		}

		return new CompanyFeatureFlagsEntry(
			'notifyUnassignedEmployees',
			CompanyFeatureFlagsEnum.notifyUnassignedEmployees,
			isEnabled && !!notifyUnassignedEmployees,
			'at: Default Time',
			false,
			true,
			CompanyFeatureFlagsTips.notifyUnassignedEmployees
		);
	}

	static autoNotifyEmployeesWithMultipleAssignments(flags: CompanyFeatureFlagsVM) {
		const { isEnabled, notifyMultipleAssignments } = flags;

		if (!isEnabled) {
			return null;
		}

		return new CompanyFeatureFlagsEntry(
			'notifyMultipleAssignments',
			CompanyFeatureFlagsEnum.notifyMultipleAssignments,
			isEnabled && !!notifyMultipleAssignments,
			'at: Default Time',
			false,
			true,
			CompanyFeatureFlagsTips.notifyMultipleAssignments
		);
	}

	static autoNotifySupervisorEntry(flags: CompanyFeatureFlagsVM) {
		const { notifySupervisors, isEnabled } = flags;

		if (!isEnabled) {
			return null;
		}

		return new CompanyFeatureFlagsEntry(
			'notifySupervisors',
			CompanyFeatureFlagsEnum.notifySupervisors,
			isEnabled && notifySupervisors,
			'at: 30 min before Default Time',
			false,
			true,
			CompanyFeatureFlagsTips.notifySupervisors
		);
	}

	static notificationPriority(flags: CompanyFeatureFlagsVM) {
		const { isEnabled, notificationPriority } = flags;

		if (!isEnabled) {
			return null;
		}
		const notificationPriorityLabel = NotificationPriorityOptions.find(({ id }) => id === notificationPriority)?.name ?? null;

		return new CompanyFeatureFlagsEntry(
			'notificationPriority',
			'Notification priority: ',
			isEnabled,
			notificationPriorityLabel,
			false,
			false
		);
	}

	static autoNotifyQAQC(flags: CompanyFeatureFlagsVM) {
		const { enableNotifyQAQC, isEnabled } = flags;

		if (!isEnabled) {
			return null;
		}

		return new CompanyFeatureFlagsEntry(
			'enableNotifyQAQC',
			CompanyFeatureFlagsEnum.notifyQAQC,
			isEnabled && enableNotifyQAQC,
			'at: Default Time',
			false,
			true,
			CompanyFeatureFlagsTips.notifyQAQC
		);
	}

	static autoNotifyDeliverableAssignee(flags: CompanyFeatureFlagsVM) {
		const { enableNotifyDeliverableAssignee, isEnabled } = flags;

		if (!isEnabled) {
			return null;
		}

		return new CompanyFeatureFlagsEntry(
			'enableNotifyDeliverableAssignee',
			CompanyFeatureFlagsEnum.notifyDeliverableAssignee,
			isEnabled && enableNotifyDeliverableAssignee,
			'at: Immediate',
			false,
			true,
			CompanyFeatureFlagsTips.notifyDeliverableAssignee
		);
	}

	static notifyTemporaryLabor(flags: CompanyFeatureFlagsVM) {
		const { isEnabled, notifyTemporaryLabor } = flags;

		if (!isEnabled) {
			return null;
		}

		return new CompanyFeatureFlagsEntry(
			'notifyTemporaryLabor',
			CompanyFeatureFlagsEnum.notifyTemporaryLabor,
			isEnabled && notifyTemporaryLabor,
			null,
			false,
			true,
			CompanyFeatureFlagsTips.notifyTemporaryLabor
		);
	}

	static notifyOnOrderCreation(flags: CompanyFeatureFlagsVM) {
		const { isEnabled, notifyOnOrderCreation, notifyOnOrderCreationUserGroup } = flags;

		if (!isEnabled) {
			return null;
		}

		return new CompanyFeatureFlagsEntry(
			'notifyOnOrderCreation',
			CompanyFeatureFlagsEnum.notifyOnOrderCreation,
			notifyOnOrderCreation,
			`to: ${notifyOnOrderCreationUserGroup?.name ?? '-'}`,
			false,
			true,
			CompanyFeatureFlagsTips.notifyOnOrderCreation
		);
	}

	static notifyForUnfinishedOrders(flags: CompanyFeatureFlagsVM) {
		const { isEnabled, notifyForUnfinishedOrders, notifyForUnfinishedOrdersUserGroup } = flags;

		if (!isEnabled) {
			return null;
		}

		return new CompanyFeatureFlagsEntry(
			'notifyForUnfinishedOrders',
			CompanyFeatureFlagsEnum.notifyForUnfinishedOrders,
			notifyForUnfinishedOrders,
			`to: ${notifyForUnfinishedOrdersUserGroup?.name ?? '-'}`,
			false,
			true,
			CompanyFeatureFlagsTips.notifyForUnfinishedOrders
		);
	}

	static notifyForOrdersTotalExpenseDaily(flags: CompanyFeatureFlagsVM) {
		const { isEnabled, notifyForOrdersTotalExpenseDaily, notifyForOrdersTotalExpenseDailyUserGroup } = flags;

		if (!isEnabled) {
			return null;
		}

		return new CompanyFeatureFlagsEntry(
			'notifyForOrdersTotalExpenseDaily',
			CompanyFeatureFlagsEnum.notifyForOrdersTotalExpenseDaily,
			notifyForOrdersTotalExpenseDaily,
			`to: ${notifyForOrdersTotalExpenseDailyUserGroup?.name ?? '-'}`,
			false,
			true,
			CompanyFeatureFlagsTips.notifyForOrdersTotalExpenseDaily
		);
	}

	static notifyForOrdersTotalExpenseWeekly(flags: CompanyFeatureFlagsVM) {
		const { isEnabled, notifyForOrdersTotalExpenseWeekly, notifyForOrdersTotalExpenseWeeklyUserGroup } = flags;

		if (!isEnabled) {
			return null;
		}

		return new CompanyFeatureFlagsEntry(
			'notifyForOrdersTotalExpenseWeekly',
			CompanyFeatureFlagsEnum.notifyForOrdersTotalExpenseWeekly,
			notifyForOrdersTotalExpenseWeekly,
			`to: ${notifyForOrdersTotalExpenseWeeklyUserGroup?.name ?? '-'}`,
			false,
			true,
			CompanyFeatureFlagsTips.notifyForOrdersTotalExpenseWeekly
		);
	}

	static notifyForOrdersTotalExpenseMonthly(flags: CompanyFeatureFlagsVM) {
		const { isEnabled, notifyForOrdersTotalExpenseMonthly, notifyForOrdersTotalExpenseMonthlyUserGroup } = flags;

		if (!isEnabled) {
			return null;
		}

		return new CompanyFeatureFlagsEntry(
			'notifyForOrdersTotalExpenseMonthly',
			CompanyFeatureFlagsEnum.notifyForOrdersTotalExpenseMonthly,
			notifyForOrdersTotalExpenseMonthly,
			`to: ${notifyForOrdersTotalExpenseMonthlyUserGroup?.name ?? '-'}`,
			false,
			true,
			CompanyFeatureFlagsTips.notifyForOrdersTotalExpenseMonthly
		);
	}

	static notifyForDuplicateOrdersMonthly(flags: CompanyFeatureFlagsVM) {
		const { isEnabled, notifyForDuplicateOrdersMonthly, notifyForDuplicateOrdersMonthlyUserGroup } = flags;

		if (!isEnabled) {
			return null;
		}

		return new CompanyFeatureFlagsEntry(
			'notifyForDuplicateOrdersMonthly',
			CompanyFeatureFlagsEnum.notifyForDuplicateOrdersMonthly,
			notifyForDuplicateOrdersMonthly,
			`to: ${notifyForDuplicateOrdersMonthlyUserGroup?.name ?? '-'}`,
			false,
			true,
			CompanyFeatureFlagsTips.notifyForDuplicateOrdersMonthly
		);
	}

	static notifyForInexpensiveOrdersWeekly(flags: CompanyFeatureFlagsVM) {
		const { isEnabled, notifyForInexpensiveOrdersWeekly, notifyForInexpensiveOrdersWeeklyUserGroup } = flags;

		if (!isEnabled) {
			return null;
		}

		return new CompanyFeatureFlagsEntry(
			'notifyForInexpensiveOrdersWeekly',
			CompanyFeatureFlagsEnum.notifyForInexpensiveOrdersWeekly,
			notifyForInexpensiveOrdersWeekly,
			`to: ${notifyForInexpensiveOrdersWeeklyUserGroup?.name ?? '-'}`,
			false,
			true,
			CompanyFeatureFlagsTips.notifyForInexpensiveOrdersWeekly
		);
	}

	static notifyForInexpensiveOrdersMonthly(flags: CompanyFeatureFlagsVM) {
		const { isEnabled, notifyForInexpensiveOrdersMonthly, notifyForInexpensiveOrdersMonthlyUserGroup } = flags;

		if (!isEnabled) {
			return null;
		}

		return new CompanyFeatureFlagsEntry(
			'notifyForInexpensiveOrdersMonthly',
			CompanyFeatureFlagsEnum.notifyForInexpensiveOrdersMonthly,
			notifyForInexpensiveOrdersMonthly,
			`to: ${notifyForInexpensiveOrdersMonthlyUserGroup?.name ?? '-'}`,
			false,
			true,
			CompanyFeatureFlagsTips.notifyForInexpensiveOrdersMonthly
		);
	}

	static notifyForExpensiveOrdersWeekly(flags: CompanyFeatureFlagsVM) {
		const { isEnabled, notifyForExpensiveOrdersWeekly, notifyForExpensiveOrdersWeeklyUserGroup } = flags;

		if (!isEnabled) {
			return null;
		}

		return new CompanyFeatureFlagsEntry(
			'notifyForExpensiveOrdersWeekly',
			CompanyFeatureFlagsEnum.notifyForExpensiveOrdersWeekly,
			notifyForExpensiveOrdersWeekly,
			`to: ${notifyForExpensiveOrdersWeeklyUserGroup?.name ?? '-'}`,
			false,
			true,
			CompanyFeatureFlagsTips.notifyForExpensiveOrdersWeekly
		);
	}

	static notifyForExpensiveOrdersMonthly(flags: CompanyFeatureFlagsVM) {
		const { isEnabled, notifyForExpensiveOrdersMonthly, notifyForExpensiveOrdersMonthlyUserGroup } = flags;

		if (!isEnabled) {
			return null;
		}

		return new CompanyFeatureFlagsEntry(
			'notifyForExpensiveOrdersMonthly',
			CompanyFeatureFlagsEnum.notifyForExpensiveOrdersMonthly,
			notifyForExpensiveOrdersMonthly,
			`to: ${notifyForExpensiveOrdersMonthlyUserGroup?.name ?? '-'}`,
			false,
			true,
			CompanyFeatureFlagsTips.notifyForExpensiveOrdersMonthly
		);
	}

	static sendScheduledNotifications(flags: CompanyFeatureFlagsVM) {
		const { isEnabled, enableScheduledNotifications } = flags;

		if (!isEnabled) {
			return null;
		}

		return new CompanyFeatureFlagsEntry(
			'enableScheduledNotifications',
			CompanyFeatureFlagsEnum.scheduledNotifications,
			isEnabled && !!enableScheduledNotifications,
			'at: Scheduled time',
			false,
			true,
			CompanyFeatureFlagsTips.scheduledNotifications
		);
	}

	static companyCredentialNotifications(credentials: CredentialVM[]) {
		return credentials.map((_credential) => new CompanyFeatureFlagsEntry(
			_credential.name,
			_credential.name,
			_credential.isEnabled,
			'at: Scheduled time',
			false,
			true,
			_credential.name
		));
	}
}

export class CompanyFeatureFlagEntries {
	general: (CompanyFeatureFlagsEntry | null)[];
	defaultSendTimes: (CompanyFeatureFlagsEntry | null)[];
	specialEmails: (CompanyFeatureFlagsEntry | null)[];
	workOrderNotifications: (CompanyFeatureFlagsEntry | null)[];
	temporaryLaborNotifications: (CompanyFeatureFlagsEntry | null)[];
	areNotificationsOn: boolean; // Redundant but useful
	credentials: Nullable<CompanyFeatureFlagsEntry[]>;

	constructor(flags: CompanyFeatureFlagsVM) {
		this.general = [
			CompanyFeatureFlagsEntry.enableNotificationsEntry(flags),
			CompanyFeatureFlagsEntry.enableAutomaticNotificationsOnWorkDaysEntry(flags),
			CompanyFeatureFlagsEntry.notificationPriority(flags),
		];

		this.defaultSendTimes = [
			...CompanyFeatureFlagsEntry.autoNotifyOnDaysEntries(flags),
		];

		this.specialEmails = [
			CompanyFeatureFlagsEntry.autoNotifyScheduleBoardEmail(flags),
			CompanyFeatureFlagsEntry.autoNotifyYesterdaysScheduleBoardEmail(flags),
			CompanyFeatureFlagsEntry.autoNotifyUnfinishedPurchaseOrderCreated(flags),
			CompanyFeatureFlagsEntry.autoNotifyPurchaseOrderWithUnreceivedItems(flags),
			CompanyFeatureFlagsEntry.notifyOnOrderCreation(flags),
			CompanyFeatureFlagsEntry.notifyForUnfinishedOrders(flags),
			CompanyFeatureFlagsEntry.notifyOnItemDepartmentLowQuantity(flags),
			CompanyFeatureFlagsEntry.autoNotifyOnItemDepartmentLowQuantity(flags),
			CompanyFeatureFlagsEntry.notifyForOrdersTotalExpenseDaily(flags),
			CompanyFeatureFlagsEntry.notifyForOrdersTotalExpenseWeekly(flags),
			CompanyFeatureFlagsEntry.notifyForOrdersTotalExpenseMonthly(flags),
			CompanyFeatureFlagsEntry.notifyForDuplicateOrdersMonthly(flags),
			CompanyFeatureFlagsEntry.notifyForInexpensiveOrdersWeekly(flags),
			CompanyFeatureFlagsEntry.notifyForInexpensiveOrdersMonthly(flags),
			CompanyFeatureFlagsEntry.notifyForExpensiveOrdersWeekly(flags),
			CompanyFeatureFlagsEntry.notifyForExpensiveOrdersMonthly(flags),
			CompanyFeatureFlagsEntry.autoNotifyFailedNotifications(flags),
			CompanyFeatureFlagsEntry.notifyPastWorkOrdersEmail(flags),
			CompanyFeatureFlagsEntry.autoNotifyOnToolRepairModify(flags),
		];
		if (flags.isSendingInvoiceNotificationsEnabled) {
			this.specialEmails.push(CompanyFeatureFlagsEntry.autoNotifyInvoiceReminderNotification(flags));
		}

		this.workOrderNotifications = [
			CompanyFeatureFlagsEntry.autoNotifyOnPublishEntry(flags),
			CompanyFeatureFlagsEntry.autoNotifyOnCancelEntry(flags),
			CompanyFeatureFlagsEntry.autoNotifyOnPauseEntry(flags),
			CompanyFeatureFlagsEntry.autoNotifyOnResumeEntry(flags),
			CompanyFeatureFlagsEntry.autoNotifyEmployeesWithMultipleAssignments(flags),
			CompanyFeatureFlagsEntry.autoNotifyUnassignedEmployeesEntry(flags),
			CompanyFeatureFlagsEntry.autoNotifySupervisorEntry(flags),
			CompanyFeatureFlagsEntry.autoNotifyQAQC(flags),
			CompanyFeatureFlagsEntry.autoNotifyDeliverableAssignee(flags),
			CompanyFeatureFlagsEntry.autoNotifyOnTimeSheetReject(flags),
			CompanyFeatureFlagsEntry.autoNotifyOnReportReject(flags),
			CompanyFeatureFlagsEntry.sendScheduledNotifications(flags),
		];

		this.areNotificationsOn = flags.isEnabled;

		this.temporaryLaborNotifications = [
			CompanyFeatureFlagsEntry.notifyTemporaryLabor(flags),
		];

		this.credentials = flags.companyCredentialSettings.length
			? CompanyFeatureFlagsEntry.companyCredentialNotifications(flags.companyCredentialSettings)
			: null;
	}
}
